import React, { useEffect, useMemo } from "react";
import { Typography, Space, Divider, Alert, Form, Button } from "antd";
import {
    ApiOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import CommonCard from "../../../global/card";
import CommonForm from "../../../global/form";
import { useGenericQuery } from "../../../../queries/use-generic-query";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import SOURCES from "../../../../utils/constants";
import { MSG_ZAPIER } from "../../../../utils/message";
import { cn } from "../../../../utils/cn";
import Spinner from "../../../global/spinner";

const { Text, Title, Link } = Typography;

const EnhancedZapierIntegration = () => {
    const [form] = Form.useForm();

    const {
        data: zapierData,
        isLoading: isDataLoading,
        refetch
    } = useGenericQuery({
        queryKey: QUERY_KEYS.zapier.lists(),
        endpoint: "zapier",
    });

    const { mutate: saveZapier, isPending: isSaving } = useGenericMutation({
        endpoint: "zapier",
        method: "POST",
        onSuccess: () => {
            refetch();
        },
        successMessage: MSG_ZAPIER.SAVE
    });

    useEffect(() => {
        if (zapierData) {
            const honeyBook = zapierData.destinations?.find(
                dest => dest.name === SOURCES.DESTINATION_HONEYBOOK
            );

            form.setFieldsValue({
                email: zapierData.email,
                password: zapierData.password,
                honeyBookEmail: honeyBook?.email,
                honeyBookPassword: honeyBook?.password,
            });
        }
    }, [zapierData, form]);

    const onFinish = (values) => {
        const payload = {
            id: zapierData?._id,
            email: values.email,
            password: values.password,
            destinations: [
                {
                    name: SOURCES.DESTINATION_HONEYBOOK,
                    email: values.honeyBookEmail,
                    password: values.honeyBookPassword,
                }
            ]
        };
        saveZapier(payload);
    };

    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            name: "zapier_header",
            span: 24,
            component: (
                <div className="flex items-center gap-2 ">
                    <ApiOutlined className="text-primary text-xl" />
                    <Title level={5} className="!mb-0">Zapier Account Credentials</Title>
                </div>
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Zapier Account Email",
            name: "email",
            placeholder: "Zapier Account Email",
            type: "email",
            required: true,
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Zapier Account Password",
            name: "password",
            placeholder: "Zapier Account Password",
            type: "password",
            required: true,
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            name: "divider_1",
            span: 24,
            component: <Divider className="my-0 border-gray-100" />
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            name: "honeybook_header",
            span: 24,
            component: (
                <div className="flex items-center gap-2 mb-0">
                    <img src="images/honeybook.png" alt="HoneyBook" className="w-8" />
                    <Title level={5} className="!mb-0">HoneyBook Account Credentials</Title>
                </div>
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Account Email",
            name: "honeyBookEmail",
            placeholder: "Account Email",
            type: "email",
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Account Password",
            name: "honeyBookPassword",
            placeholder: "Account Password",
            type: "password",
            span: 12,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            name: "note_section",
            span: 24,
            component: (
                <Alert
                    className="mt-0 bg-blue-50/50 border-blue-100 rounded-xl"
                    message={
                        <div className="flex flex-col gap-2 p-1">
                            <div className="flex items-center gap-2 text-blue-800 font-bold overflow-hidden">
                                <InfoCircleOutlined />
                                <span>Note:</span>
                            </div>
                            <Text className="text-gray-700 leading-relaxed">
                                We will inform you once <Text strong>Zapier</Text> setup has been completed by our team.
                                If you don’t already have a Zapier account, please {" "}
                                <Link
                                    href="https://zapier.com/sign-up"
                                    target="_blank"
                                    className="text-red-600 font-bold hover:text-red-700 underline underline-offset-4 decoration-2"
                                >
                                    create a free account
                                </Link>.
                                A <Text strong className="text-red-600">Zapier Professional</Text> plan is required for integration.
                            </Text>
                        </div>
                    }
                    type="info"
                />
            )
        }
    ], []);

    return (
        <CommonCard
            isLoading={{
                show: isDataLoading,
                loaderCmp: (
                    <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-20 rounded-xl flex flex-col justify-center items-center transition-all duration-300">
                        <div className="flex flex-col items-center gap-3">
                            <div className="relative size-12 flex items-center justify-center">
                                <div className="absolute inset-0 rounded-full border-4 border-primary/10" />
                                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin" />
                            </div>
                            <Text className="text-primary font-semibold tracking-wide animate-pulse">
                                Loading Configuration...
                            </Text>
                        </div>
                    </div>
                )
            }}
            className={isDataLoading && "!min-h-[500px]"}
        >
            <CommonForm
                form={form}
                data={formData}
                onFinish={onFinish}
                className="space-y-0"
            />

            <div className="mt-0 pt-6 border-t border-gray-100 flex justify-end">
                <Space size="middle">
                    <Form.Item className="!mb-0">
                        <Button
                            onClick={() => form.submit()}
                            disabled={isSaving}
                            className={cn(
                                "!text-white !border-primary hover:!border-primary hover:!bg-primary !bg-primary h-10",
                            )}
                        >
                            {isSaving && <Spinner className={"!border-white"} />} Save Configuration
                        </Button>
                    </Form.Item>
                </Space>
            </div>
        </CommonCard>
    );
};

export default EnhancedZapierIntegration;